/**
 * E-GTCC-VIEW-TEN1, tab 2 — "Tenant 360°".
 *
 * Reference: `GTCC_REFERENCE_2026-09-01.html:696-716` — the `.grid-main`, left
 * the panel "TENANT 360° — <NAME>" with `.fresh` and eleven `.row`s.
 *
 * The Owner order for these rows is literal: "jede Row klappt ihre Karte aus
 * der alten Konsole als Detail aus (bestehende Karten-Komponenten
 * wiederverwenden, nur in Referenz-Bausteinen gerahmt)". So every row here is
 * the reference's dot + label + mono summary, and its detail is the gtcc1 card
 * verbatim. Nothing in a detail was rewritten; the sections only gained an
 * `export` (`GtccTenantsView.tsx`), and the header card was split into
 * identity and domain readiness because the reference lists those as two rows
 * while gtcc1 stacks them in one.
 *
 * The mono summary of a row is measured, never decorative. Where the reference
 * shows a value this view cannot measure, the row reads "nicht messbar"
 * (Owner decisions P9/P10) — the card behind it still shows everything it can
 * measure itself.
 *
 * Row "Abo" is the one place the reference asks for verbs that do not exist:
 * `[Mark as trial…]` and `[Set valid until…]` have no admin endpoint (the
 * lifecycle route knows pause/resume/suspend/cancel only,
 * `tenantLifecycle.ts:1`). They are therefore not drawn as dead buttons; the
 * row states the fact instead. No new write path.
 */
import { EmptyState, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";

import {
  AiCapabilitiesSection,
  AiUsageSection,
  EntitlementsSection,
  InvitationsSection,
  LaunchStatusSection,
  MembersSection,
  TenantDomainReadinessSection,
  TenantIdentitySection,
  TenantLifecycleSection,
} from "../GtccTenantsView.js";
import { TenantDevicesCard } from "../../../../tenants/TenantDevicesCard.js";
import { TenantVehicleAppearanceCard } from "../../../../tenants/TenantVehicleAppearanceCard.js";
import {
  fetchTenDevicesSummary,
  fetchTenInvitations,
  fetchTenLaunchStatus,
  fetchTenMembers,
  fetchTenTenant,
  type TenLaunchStatus,
  type TenTenantDetail,
} from "../../../api/ten.js";
import {
  FleetFreshBadge,
  NotMeasurable,
  type FleetTone,
} from "../../cockpit/components/FleetPrimitives.js";
import {
  GRID_COLUMN_STYLE,
  GRID_MAIN_STYLE,
  TEN_QUERY_OPTIONS,
  TenMutedNote,
  TenPanel,
  TenRow,
  entitlementsValueText,
  launchValueText,
  lifecycleTokenText,
  lifecycleTone,
  type TenTabProps,
} from "../components/TenPanels.js";

const UNLIMITED = "∞";

function limitOf(value: number | null | undefined): string {
  return typeof value === "number" ? String(value) : UNLIMITED;
}

/**
 * Der Gesamtstatus der Launch-Checkliste. "ready" ist gruen, "blocked" rot,
 * alles andere eine Warnung — ein unbekannter Wert bleibt grau statt gesund
 * (Owner-Entscheid P8).
 */
function launchTone(overall: string | undefined): FleetTone {
  if (overall === "ready") return "ok";
  if (overall === "blocked") return "crit";
  if (overall === "needs_action" || overall === "manual") return "warn";
  return "muted";
}

export function Tenant360Tab(props: TenTabProps): ReactNode {
  const { t, locale } = useLocale();
  const tenantId = props.selectedTenantId;

  const enabled = tenantId !== null;
  const id = tenantId ?? "";

  const detail = useQuery({
    queryKey: ["gtcc", "ten", "view", "tenant", id],
    queryFn: ({ signal }) => fetchTenTenant(id, signal),
    enabled,
    ...TEN_QUERY_OPTIONS,
  });
  const launch = useQuery({
    queryKey: ["gtcc", "ten", "view", "launch", id],
    queryFn: ({ signal }) => fetchTenLaunchStatus(id, signal),
    enabled,
    ...TEN_QUERY_OPTIONS,
  });
  const devices = useQuery({
    queryKey: ["gtcc", "ten", "view", "devices", id],
    queryFn: ({ signal }) => fetchTenDevicesSummary(id, signal),
    enabled,
    ...TEN_QUERY_OPTIONS,
  });
  const members = useQuery({
    queryKey: ["gtcc", "ten", "view", "members", id],
    queryFn: ({ signal }) => fetchTenMembers(id, signal),
    enabled,
    ...TEN_QUERY_OPTIONS,
  });
  const invitations = useQuery({
    queryKey: ["gtcc", "ten", "view", "invitations", id],
    queryFn: ({ signal }) => fetchTenInvitations(id, signal),
    enabled,
    ...TEN_QUERY_OPTIONS,
  });

  if (tenantId === null) {
    return (
      <div data-testid="gtcc-ten-360-no-tenant">
        <EmptyState
          title={t("admin.gtcc.ten.t360.no_tenant_title")}
          body={t("admin.gtcc.ten.t360.no_tenant_body")}
        />
      </div>
    );
  }

  /*
   * Die Zusammenfassungen der Zeilen kommen aus den Antworten, die schon da
   * sind. Fehlt eine Antwort oder ist sie ein Fehler, liest die Zeile "nicht
   * messbar" — die Karte im Detail sagt dann, woran es lag.
   */
  const tenant: TenTenantDetail | null =
    detail.data !== undefined && detail.data.ok ? detail.data.data : null;
  const launchData: TenLaunchStatus | null =
    launch.data !== undefined && launch.data.ok ? launch.data.data : null;
  const deviceTotal =
    devices.data !== undefined && devices.data.ok ? devices.data.data.total : null;
  const memberCount =
    members.data !== undefined && members.data.ok ? members.data.data.length : null;
  const openInvitations =
    invitations.data !== undefined && invitations.data.ok
      ? invitations.data.data.filter((row) => row.status === "pending").length
      : null;

  const unmeasured = <NotMeasurable />;
  const title = t("admin.gtcc.ten.t360.panel", {
    name: tenant?.displayName ?? tenantId,
  });

  const validUntil =
    tenant?.validUntil === undefined || tenant.validUntil === null
      ? null
      : new Intl.DateTimeFormat(locale, { dateStyle: "short" }).format(new Date(tenant.validUntil));

  const entitlements = tenant?.resolvedEntitlements ?? tenant?.entitlements ?? null;

  return (
    <div style={GRID_MAIN_STYLE}>
      <div style={GRID_COLUMN_STYLE}>
        <TenPanel
          testId="gtcc-ten-360"
          title={title}
          note={
            launchData?.computedAt ? (
              <FleetFreshBadge
                seconds={undefined}
                measuredAt={launchData.computedAt}
                testId="gtcc-ten-360-fresh"
              />
            ) : (
              <NotMeasurable testId="gtcc-ten-360-fresh" />
            )
          }
        >
          <Stack gap={0}>
            <TenRow
              testId="gtcc-ten-360-launch"
              tone={launchTone(launchData?.overall)}
              label={t("admin.gtcc.ten.t360.row.launch")}
              value={launchValueText(t, launchData) ?? unmeasured}
            >
              <LaunchStatusSection tenantId={tenantId} />
            </TenRow>

            <TenRow
              testId="gtcc-ten-360-identity"
              tone={lifecycleTone(tenant?.subscriptionStatus)}
              label={t("admin.gtcc.ten.t360.row.identity")}
              value={
                tenant === null
                  ? unmeasured
                  : `${lifecycleTokenText(t, tenant.subscriptionStatus)} · ${tenant.type ?? "—"}`
              }
            >
              <Stack gap={3}>
                <TenantIdentitySection tenantId={tenantId} />
                <TenantLifecycleSection tenantId={tenantId} />
              </Stack>
            </TenRow>

            <TenRow
              testId="gtcc-ten-360-subscription"
              tone={lifecycleTone(tenant?.subscriptionStatus)}
              label={t("admin.gtcc.ten.t360.row.subscription")}
              value={
                tenant === null
                  ? unmeasured
                  : validUntil === null
                    ? t("admin.gtcc.ten.t360.valid_until_none")
                    : t("admin.gtcc.ten.t360.valid_until", { date: validUntil })
              }
            >
              <TenMutedNote testId="gtcc-ten-360-subscription-note">
                {t("admin.gtcc.ten.t360.subscription_no_endpoint")}
              </TenMutedNote>
            </TenRow>

            <TenRow
              testId="gtcc-ten-360-entitlements"
              tone="info"
              label={t("admin.gtcc.ten.t360.row.entitlements")}
              value={
                entitlements === null || entitlements === undefined
                  ? unmeasured
                  : (entitlementsValueText(t, entitlements) ?? unmeasured)
              }
            >
              <EntitlementsSection tenantId={tenantId} />
            </TenRow>

            <TenRow
              testId="gtcc-ten-360-ai"
              tone="info"
              label={t("admin.gtcc.ten.t360.row.ai")}
              value={unmeasured}
            >
              <Stack gap={3}>
                <AiCapabilitiesSection tenantId={tenantId} />
                <AiUsageSection tenantId={tenantId} />
              </Stack>
            </TenRow>

            <TenRow
              testId="gtcc-ten-360-quotas"
              tone="muted"
              label={t("admin.gtcc.ten.t360.row.quotas")}
              value={
                tenant === null
                  ? unmeasured
                  : `${limitOf(tenant.maxDevices)} / ${limitOf(tenant.maxVehicles)} / ${limitOf(tenant.maxUsers)}`
              }
            />

            <TenRow
              testId="gtcc-ten-360-devices"
              tone="muted"
              label={t("admin.gtcc.ten.t360.row.devices")}
              value={deviceTotal === null ? unmeasured : String(deviceTotal)}
            >
              <TenantDevicesCard tenantId={tenantId} />
            </TenRow>

            <TenRow
              testId="gtcc-ten-360-appearance"
              tone="muted"
              label={t("admin.gtcc.ten.t360.row.appearance")}
              value={unmeasured}
            >
              <TenantVehicleAppearanceCard tenantId={tenantId} />
            </TenRow>

            <TenRow
              testId="gtcc-ten-360-members"
              tone="muted"
              label={t("admin.gtcc.ten.t360.row.members")}
              value={memberCount === null ? unmeasured : String(memberCount)}
            >
              <MembersSection tenantId={tenantId} />
            </TenRow>

            <TenRow
              testId="gtcc-ten-360-onboard"
              tone="info"
              label={t("admin.gtcc.ten.t360.row.onboard")}
              value={openInvitations === null ? unmeasured : String(openInvitations)}
            >
              <InvitationsSection tenantId={tenantId} />
            </TenRow>

            <TenRow
              testId="gtcc-ten-360-domain"
              tone="info"
              label={t("admin.gtcc.ten.t360.row.domain")}
              value={tenant?.slug ?? unmeasured}
            >
              <TenantDomainReadinessSection tenantId={tenantId} />
            </TenRow>
          </Stack>
        </TenPanel>
      </div>

      <div style={GRID_COLUMN_STYLE}>
        <TenPanel testId="gtcc-ten-360-aside" title={t("admin.gtcc.ten.t360.aside")}>
          <TenMutedNote testId="gtcc-ten-360-aside-note">
            {t("admin.gtcc.ten.t360.aside_note")}
          </TenMutedNote>
        </TenPanel>
      </div>
    </div>
  );
}
